"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getTopCompanies } from "../../apis/public";
import Api from "../../utils/Api";
import CompanyLogo from "../CompanyLogo";

const TopCompanies = () => {
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();

  const [companiesData, setCompaniesData] = useState([]);
  const [cities, setCities] = useState({});

  // 1. Récupération des entreprises
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await getTopCompanies();
        setCompaniesData(data);
      } catch (error) {
        console.error("Error fetching top companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  // 2. Récupération des noms de villes via /data/cities/:id (même approche que FeaturedJobs)
  useEffect(() => {
    const fetchCityNames = async () => {
      const newCities = {};

      for (const company of companiesData) {
        const cityId = company.address?.city;

        if (cityId && !isNaN(cityId) && !cities[cityId]) {
          try {
            const res = await Api.get(`/data/cities/${cityId}`);
            newCities[cityId] = {
              fr: res.data.name || res.data.name_fr,
              ar: res.data.name_ar
            };
          } catch (err) {
            console.error("Error fetching city", cityId, err);
          }
        }
      }

      if (Object.keys(newCities).length > 0) {
        setCities(prev => ({ ...prev, ...newCities }));
      }
    };

    if (companiesData.length > 0) {
      fetchCityNames();
    }
  }, [companiesData]);

  const companies = companiesData.map((company) => {
    const cityId = company.address?.city;
    const isArabic = i18n.language === 'ar';

    let locationDisplay = '';

    if (cityId && !isNaN(cityId)) {
      if (cities[cityId]) {
        locationDisplay = isArabic
          ? (cities[cityId].ar || cities[cityId].fr)
          : (cities[cityId].fr || cities[cityId].ar);
      }
    } else {
      locationDisplay = cityId || '';
    }

    return {
      id: company._id,
      name: company.company_name,
      loc: locationDisplay,
      pos: company.openPositions || 0,
      company: company
    };
  });

  return (
    <section className="py-24 bg-gray-50 dark:bg-gray-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12">
          {t('topCompanies.title')} <span className="text-yellow-500">{t('topCompanies.titleHighlight')}</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company, idx) => (
            <motion.div
              key={company.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(`/company/${company.id}`)}
              className="flex items-center gap-4 p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center overflow-hidden shadow-sm">
                <CompanyLogo company={company.company} className="w-full h-full" fallbackSize="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{company.name}</h3>
                {company.loc !== '' && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                    <MapPin size={14} /> {company.loc}
                  </div>
                )}
              </div>
              <div className="text-right">
                <span className="block text-xl font-bold text-gray-900 dark:text-white">{company.pos}</span>
                <span className="text-xs text-gray-500">{t('topCompanies.openPositions')}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopCompanies;